import { IPublicClientApplication } from '@azure/msal-browser';
import * as XLSX from 'xlsx';
import { dvList, dvCreate } from '../api/dataverseClient';
import { Project } from '../types';
import { getProjectTasks } from './taskService';
import { getProjectIssues } from './issueService';
import { getProjectRisks } from './riskService';
import { getProjectMilestones } from './milestoneService';
import { getProjectTimeEntries } from './timeService';
import { getProjectBudgets, getProjectExpenses } from './budgetService';

// ── Picklist values (verified via EntityDefinitions on prod) ──────────
export const ExportType = {
  TaskList:          100000000,
  TimeReport:        100000001,
  IssueRegister:     100000002,
  RiskRegister:      100000003,
  BudgetReport:      100000004,
  ResourceReport:    100000005,
  PortfolioExport:   100000006,
  FullProjectExport: 100000007,
} as const;
export const EXPORT_TYPE_LABEL: Record<number, string> = {
  [ExportType.TaskList]:          'Task List',
  [ExportType.TimeReport]:        'Time Report',
  [ExportType.IssueRegister]:     'Issue Register',
  [ExportType.RiskRegister]:      'Risk Register',
  [ExportType.BudgetReport]:      'Budget Report',
  [ExportType.ResourceReport]:    'Resource Report',
  [ExportType.PortfolioExport]:   'Portfolio Export',
  [ExportType.FullProjectExport]: 'Full Project',
};

export const ExportFormat = {
  Excel: 100000000, PDF: 100000001, CSV: 100000002, JSON: 100000003, ZIP: 100000004,
} as const;
export const FORMAT_LABEL: Record<number, string> = {
  [ExportFormat.Excel]: 'Excel', [ExportFormat.PDF]: 'PDF', [ExportFormat.CSV]: 'CSV',
  [ExportFormat.JSON]: 'JSON', [ExportFormat.ZIP]: 'ZIP',
};

export const ExportStatus = {
  Pending: 100000000, InProgress: 100000001, Completed: 100000002, Failed: 100000003,
} as const;
export const STATUS_LABEL: Record<number, string> = {
  [ExportStatus.Pending]: 'Pending', [ExportStatus.InProgress]: 'In Progress',
  [ExportStatus.Completed]: 'Completed', [ExportStatus.Failed]: 'Failed',
};

export interface ExportJob {
  dagrun_pm_exportjobid: string;
  dagrun_pm_name: string;
  dagrun_pm_exporttype: number | null;
  dagrun_pm_format: number | null;
  dagrun_pm_status: number | null;
  dagrun_pm_fileurl?: string | null;
  dagrun_pm_expireson?: string | null;
  createdon?: string;
}

const JOB_FIELDS = [
  'dagrun_pm_exportjobid', 'dagrun_pm_name', 'dagrun_pm_exporttype',
  'dagrun_pm_format', 'dagrun_pm_status', 'dagrun_pm_fileurl', 'dagrun_pm_expireson', 'createdon',
];

export async function getProjectExportJobs(pca: IPublicClientApplication, projectId: string): Promise<ExportJob[]> {
  return dvList<ExportJob>(pca, 'dagrun_pm_exportjobs', {
    select: JOB_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'createdon desc',
    top: 50,
  });
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

async function recordJob(
  pca: IPublicClientApplication, projectId: string,
  exportType: number, format: number, name: string,
): Promise<void> {
  try {
    await dvCreate(pca, 'dagrun_pm_exportjobs', {
      dagrun_pm_name: name,
      dagrun_pm_exporttype: exportType,
      dagrun_pm_format: format,
      dagrun_pm_status: ExportStatus.Completed,
      dagrun_pm_fileurl: 'client-generated',
      dagrun_pm_expireson: new Date(Date.now() + SEVEN_DAYS_MS).toISOString(),
      dagrun_pm_scopejson: JSON.stringify({ projectId }),
      'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
    });
  } catch {
    // The download already happened; a failed job-record write is non-fatal.
  }
}

function downloadXlsx(rows: Record<string, unknown>[], sheetName: string, filename: string): void {
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ '(no rows)': '' }]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
}

function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeName(project: Project): string {
  return (project.dagrun_pm_name || 'project').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
}

// ── Per-type exporters ────────────────────────────────────────────────

export async function exportTaskList(pca: IPublicClientApplication, project: Project): Promise<void> {
  const tasks = await getProjectTasks(pca, project.dagrun_pm_projectid);
  const rows = tasks.map(t => ({
    Title: t.dagrun_pm_name,
    Status: t.dagrun_pm_status,
    Priority: t.dagrun_pm_priority ?? '',
    Assignee: t.dagrun_pm_assigneename ?? '',
    'Start Date': t.dagrun_pm_startdate ?? '',
    'Due Date': t.dagrun_pm_duedate ?? '',
    'Percent Complete': t.dagrun_pm_percentcomplete ?? 0,
    'Estimated Hours': t.dagrun_pm_estimatedhours ?? '',
    'Completed Hours': t.dagrun_pm_effortcompletedhours ?? '',
  }));
  downloadXlsx(rows, 'Tasks', `tasks-${safeName(project)}.xlsx`);
  await recordJob(pca, project.dagrun_pm_projectid, ExportType.TaskList, ExportFormat.Excel, `Task List — ${project.dagrun_pm_name}`);
}

export async function exportIssueRegister(pca: IPublicClientApplication, project: Project): Promise<void> {
  const issues = await getProjectIssues(pca, project.dagrun_pm_projectid);
  const rows = issues.map(i => ({
    Title: i.dagrun_pm_name,
    Severity: i.dagrun_pm_severity,
    Status: i.dagrun_pm_status,
    Owner: i.dagrun_pm_ownerid ?? '',
    'Client Visible': i.dagrun_pm_clientvisible ? 'Yes' : 'No',
    'Resolved On': i.dagrun_pm_resolvedon ?? '',
    Resolution: i.dagrun_pm_resolutionnotes ?? '',
  }));
  downloadXlsx(rows, 'Issues', `issues-${safeName(project)}.xlsx`);
  await recordJob(pca, project.dagrun_pm_projectid, ExportType.IssueRegister, ExportFormat.Excel, `Issue Register — ${project.dagrun_pm_name}`);
}

export async function exportRiskRegister(pca: IPublicClientApplication, project: Project): Promise<void> {
  const risks = await getProjectRisks(pca, project.dagrun_pm_projectid);
  const rows = risks.map(r => ({
    Title: r.dagrun_pm_name,
    Probability: r.dagrun_pm_probability,
    Impact: r.dagrun_pm_impact,
    Score: r.dagrun_pm_riskscore ?? r.dagrun_pm_priorityscore ?? '',
    Status: r.dagrun_pm_status,
    Owner: r.dagrun_pm_ownername ?? '',
    Mitigation: r.dagrun_pm_mitigationplan ?? '',
  }));
  downloadXlsx(rows, 'Risks', `risks-${safeName(project)}.xlsx`);
  await recordJob(pca, project.dagrun_pm_projectid, ExportType.RiskRegister, ExportFormat.Excel, `Risk Register — ${project.dagrun_pm_name}`);
}

export async function exportTimeReport(pca: IPublicClientApplication, project: Project): Promise<void> {
  const entries = await getProjectTimeEntries(pca, project.dagrun_pm_projectid);
  const rows = entries.map(e => ({
    Description: e.dagrun_pm_name,
    User: e.dagrun_pm_username ?? '',
    Date: e.dagrun_pm_logdate ?? '',
    Hours: e.dagrun_pm_hours ?? 0,
    Billable: e.dagrun_pm_isbillable ? 'Yes' : 'No',
    Notes: e.dagrun_pm_notes ?? '',
  }));
  downloadXlsx(rows, 'Time', `time-${safeName(project)}.xlsx`);
  await recordJob(pca, project.dagrun_pm_projectid, ExportType.TimeReport, ExportFormat.Excel, `Time Report — ${project.dagrun_pm_name}`);
}

export async function exportBudgetReport(pca: IPublicClientApplication, project: Project): Promise<void> {
  const [budgets, expenses] = await Promise.all([
    getProjectBudgets(pca, project.dagrun_pm_projectid),
    getProjectExpenses(pca, project.dagrun_pm_projectid),
  ]);
  const budgetRows = budgets.map(b => ({
    Type: 'Budget', Name: b.dagrun_pm_name, Amount: b.dagrun_pm_allocatedamount ?? 0, Currency: b.dagrun_pm_currency ?? '',
  }));
  const expenseRows = expenses.map(e => ({
    Type: 'Expense', Name: e.dagrun_pm_name, Amount: e.dagrun_pm_amount ?? 0, Currency: e.dagrun_pm_currency ?? '',
  }));
  downloadXlsx([...budgetRows, ...expenseRows], 'Budget', `budget-${safeName(project)}.xlsx`);
  await recordJob(pca, project.dagrun_pm_projectid, ExportType.BudgetReport, ExportFormat.Excel, `Budget Report — ${project.dagrun_pm_name}`);
}

export async function exportFullProject(pca: IPublicClientApplication, project: Project): Promise<void> {
  const pid = project.dagrun_pm_projectid;
  const [tasks, issues, risks, milestones] = await Promise.all([
    getProjectTasks(pca, pid).catch(() => []),
    getProjectIssues(pca, pid).catch(() => []),
    getProjectRisks(pca, pid).catch(() => []),
    getProjectMilestones(pca, pid).catch(() => []),
  ]);
  const payload = {
    project: { id: pid, name: project.dagrun_pm_name },
    exportedOn: new Date().toISOString(),
    tasks, issues, risks, milestones,
  };
  downloadJson(payload, `project-${safeName(project)}.json`);
  await recordJob(pca, pid, ExportType.FullProjectExport, ExportFormat.JSON, `Full Project — ${project.dagrun_pm_name}`);
}
